/**
 * Контракты отчёта по накопленному навыку (ТЗ UX-07).
 *
 * Экран результата показывает слабые зоны одного занятия и только сразу
 * после него. Этого мало: клавиша, которая не даётся третью неделю, и
 * клавиша, споткнувшаяся один раз сегодня, выглядят там одинаково. Отчёт
 * показывает накопленное состояние — всё, что пользователь набирал, с долей
 * ошибок, задержкой, освоенностью и сроком следующего повторения.
 *
 * Отчёт ничего не считает заново: все значения уже лежат в `MasteryStat`.
 * Он только раскладывает их по видам материала и упорядочивает.
 */

import type { MasteryStat, MasteryUnit } from '@type2x/shared-contracts';
import type { LearningConfig, PriorityContext } from '@type2x/learning-engine';

/**
 * Разделы отчёта.
 *
 * Три из них требует UX-07 — клавиши, сочетания, слова. Переходы между
 * раскладками стоят отдельно: это не материал, а движение между двумя
 * материалами, и в списке сочетаний оно выглядело бы сочетанием.
 */
export const REPORT_SECTIONS = ['keys', 'combinations', 'words', 'switches'] as const;
export type ReportSectionId = (typeof REPORT_SECTIONS)[number];

/** Строка отчёта: одна учебная единица и что о ней известно. */
export interface ReportRow {
  readonly unitId: string;
  /** Содержание единицы: символ, сочетание, слово или направление перехода. */
  readonly content: string;
  readonly attempts: number;
  /** Доля ошибок, 0..1. */
  readonly errorRate: number;
  readonly medianLatencyMs: number;
  /** Оценка освоенности, 0..1. */
  readonly masteryScore: number;
  /** Календарная метка следующего повторения (ISO 8601). */
  readonly nextReviewAt: string;
  /** Приоритет, по которому строка стоит в отчёте на своём месте. */
  readonly priority: number;
  /** Освоена ли единица: порог тот же, что и во всём остальном приложении. */
  readonly mastered: boolean;
}

/** Раздел отчёта. */
export interface ReportSection {
  readonly id: ReportSectionId;
  /**
   * Все строки раздела в порядке убывания приоритета.
   *
   * Раздел не усечён: сколько показать, решает экран, и решение это зависит
   * от того, что пользователь ищет. Отрезать здесь значило бы прятать от него
   * то, что он ищет, ещё до того, как он спросил.
   */
  readonly rows: readonly ReportRow[];
}

/**
 * Что показывать в разделе.
 *
 * Состояния считаются теми же величинами, что и во всём приложении: слабое —
 * не освоенное, а не «похуже остальных».
 */
export const REPORT_FILTERS = ['all', 'weak', 'due', 'mastered'] as const;
export type ReportFilter = (typeof REPORT_FILTERS)[number];

/** Чего просит пользователь от раздела. */
export interface SectionQuery {
  /** Поисковый запрос по содержанию единицы. Пустая строка — без поиска. */
  readonly query: string;
  readonly filter: ReportFilter;
  /** Номер страницы, считая с единицы. Вне диапазона — прижимается к нему. */
  readonly page: number;
  /** Момент, относительно которого решается, наступил ли срок повторения. */
  readonly now: string;
}

/** Что раздел показывает по запросу. */
export interface SectionView {
  /** Строки запрошенной страницы. */
  readonly rows: readonly ReportRow[];
  /** Сколько строк подошло под запрос и фильтр — до разбиения на страницы. */
  readonly matched: number;
  /** Сколько строк в разделе всего. */
  readonly total: number;
  /** Номер показанной страницы: не тот, что просили, если его не существует. */
  readonly page: number;
  /** Сколько страниц вышло. Не меньше одной, даже когда показывать нечего. */
  readonly pages: number;
}

/** Отчёт целиком. */
export interface SkillReport {
  readonly sections: readonly ReportSection[];
  /**
   * Сколько единиц пользователь ещё ни разу не набирал.
   *
   * Они существуют — их создал какой-нибудь режим, — но говорить о них
   * нечего: доля ошибок «ноль из нуля» не значит ничего.
   */
  readonly untouched: number;
  /**
   * Момент, на который снят отчёт.
   *
   * Нужен не для показа: по нему решается, наступил ли срок повторения. Брать
   * время в момент показа значило бы, что фильтр «к повторению» меняется под
   * рукой у пользователя.
   */
  readonly builtAt: string;
}

/** Исходные данные отчёта. */
export interface ReportInput {
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly stats: Readonly<Record<string, MasteryStat>>;
  readonly config: LearningConfig;
  /**
   * Контекст расчёта приоритета.
   *
   * Передаётся снаружи: он зависит от раскладки и истории контрольных
   * прогонов, а их знает слой оркестрации, а не отчёт
   * (docs/ARCHITECTURE.md §5).
   */
  readonly context: PriorityContext;
  /** Момент снятия отчёта (ISO 8601). */
  readonly now: string;
}

/**
 * Сколько строк на странице раздела.
 *
 * Клавиш десятки, слов — сотни: показывать всё разом не стоит. Но и обрывать
 * список на двадцати строках, как было до появления страниц, значит спрятать
 * остальное навсегда.
 */
export const ROWS_PER_PAGE = 20;
